import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {AudioPlayerObject} from '@Libraries/audio-player/audio-player.types';
import {getItemPageEndTime, isSlideshowItemObject, isVideoItemObject} from '@PosterWhiteboard/items/item/item.library';
import {isVideoLoopingEnabledForItem} from '@PosterWhiteboard/items/video-item/video-item.types';
import {getHybridVideoResize} from '@PosterWhiteboard/page/video-timeline-resize.library';
import type {TimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getAudioTimelineResizeBounds, getGroupResizedSpan, getVideoTimelineResizeBounds, UNBOUNDED_TIMELINE_RESIZE} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getSlideshowTimelineResizeBounds} from '@PosterWhiteboard/items/slideshow-item/slideshow-slides.class';
import {DEFAULT_SLIDE_DURATION} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type {ResizeCalculationParams, ResizeCalculationResult} from '../components/timeline-item/timeline-item.types';

/**
 * The object-side bar model: per-kind timeline math computed from serialized
 * `*Object` data, mirroring the class-side overrides (`getTimelineResizeBounds`)
 * so gesture snapshots, single-bar resizes, and commits all agree. Add a new
 * clip kind's math HERE and in its class override — nowhere else.
 */

/** Object-side mirror of `Item.getTimelineResizeBounds` and its subclass overrides. */
export const getItemBarResizeBounds = (itemObject: ItemObject): TimelineResizeBounds => {
  if (isVideoItemObject(itemObject)) {
    return getVideoTimelineResizeBounds({startTime: itemObject.onPageStartTime, endTime: getItemPageEndTime(itemObject)}, itemObject);
  }
  if (isSlideshowItemObject(itemObject)) {
    const firstSlide = itemObject.slides.slidesHashMap[itemObject.slides.slidesOrder[0]];
    const lastSlide = itemObject.slides.slidesHashMap[itemObject.slides.slidesOrder[itemObject.slides.slidesOrder.length - 1]];
    if (firstSlide === undefined || lastSlide === undefined) {
      return UNBOUNDED_TIMELINE_RESIZE;
    }
    return getSlideshowTimelineResizeBounds(
      {startTime: itemObject.onPageStartTime, endTime: getItemPageEndTime(itemObject)},
      {
        isMultiSlide: itemObject.slides.slidesOrder.length > 1,
        // Older serializations may omit slideDuration; the slide classes default it, so mirror that default here.
        firstSlideDuration: firstSlide.slideDuration ?? DEFAULT_SLIDE_DURATION,
        lastSlideDuration: lastSlide.slideDuration ?? DEFAULT_SLIDE_DURATION,
      }
    );
  }
  return UNBOUNDED_TIMELINE_RESIZE;
};

/**
 * Single-bar resize span derived from the bar's resize bounds — the same clamps the
 * group-resize path applies, so single-bar previews and group gestures agree by
 * construction instead of by five hand-rolled per-kind calculators.
 */
export const calculateResizeFromBounds = (params: ResizeCalculationParams, bounds: TimelineResizeBounds): ResizeCalculationResult => {
  return getGroupResizedSpan({startTime: params.originalStartTime, endTime: params.originalEndTime}, bounds, params.handle, params.deltaPixels / params.pixelsPerSecond);
};

type ResizeCalculation = (params: ResizeCalculationParams) => ResizeCalculationResult;

/** Bounds-derived resize calculation for an item-track bar; identity while the object hasn't loaded yet. */
export const createItemBarResizeCalculation = (itemObject: ItemObject | undefined): ResizeCalculation => {
  return (params: ResizeCalculationParams): ResizeCalculationResult => {
    if (!itemObject) {
      return {startTime: params.originalStartTime, endTime: params.originalEndTime};
    }
    if (isVideoItemObject(itemObject) && isVideoLoopingEnabledForItem(itemObject)) {
      // Loop-capable videos resize on the hybrid trim/loop continuum; its span
      // output matches the bounds path, so the modifying state stays span-only.
      const resized = getHybridVideoResize(
        {...itemObject, onPageStartTime: params.originalStartTime, onPageEndTime: params.originalEndTime},
        params.handle,
        params.deltaPixels / params.pixelsPerSecond
      );
      return {startTime: resized.onPageStartTime, endTime: resized.onPageEndTime};
    }
    return calculateResizeFromBounds(params, getItemBarResizeBounds(itemObject));
  };
};

/** Bounds-derived resize calculation for an audio bar; identity while the clip hasn't loaded yet. */
export const createAudioBarResizeCalculation = (audioPlayer: AudioPlayerObject | undefined): ResizeCalculation => {
  return (params: ResizeCalculationParams): ResizeCalculationResult => {
    if (!audioPlayer) {
      return {startTime: params.originalStartTime, endTime: params.originalEndTime};
    }
    return calculateResizeFromBounds(params, getAudioTimelineResizeBounds({startTime: params.originalStartTime, endTime: params.originalEndTime}, audioPlayer));
  };
};
